import { gql } from '@apollo/client';

// Authentication Mutations
export const AUTHENTICATE_WITH_GOOGLE = gql`
  mutation AuthenticateWithGoogle($input: GoogleAuthInput!) {
    authenticateWithGoogle(input: $input) {
      success
      user {
        id
        email
        name
        avatarURL
        onboardingCompleted
      }
      organizations {
        id
        name
        role
      }
      tokens {
        accessToken
        refreshToken
        expiresIn
      }
      requiresOnboarding
      error
    }
  }
`;

export const SIGN_OUT = gql`
  mutation SignOut {
    signOut {
      success
    }
  }
`;

export const REFRESH_AUTH_TOKEN = gql`
  mutation RefreshAuthToken($refreshToken: String!) {
    refreshAuthToken(refreshToken: $refreshToken) {
      success
      tokens {
        accessToken
        refreshToken
        expiresIn
      }
      error
    }
  }
`;

export const UPDATE_TUTORIAL_PROGRESS = gql`
  mutation UpdateTutorialProgress($input: UpdateTutorialProgressInput!) {
    updateTutorialProgress(input: $input) {
      dashboard {
        version
        status
        updatedAt
      }
      livestreamModerator {
        version
        status
        updatedAt
      }
    }
  }
`;

// Email/Password Authentication Mutations
export const SEND_VERIFICATION_CODE = gql`
  mutation SendVerificationCode($input: SendVerificationCodeInput!) {
    sendVerificationCode(input: $input) {
      success
      message
      error
    }
  }
`;

export const SIGN_UP_WITH_EMAIL = gql`
  mutation SignUpWithEmail($input: EmailSignUpInput!) {
    signUpWithEmail(input: $input) {
      success
      user {
        id
        email
        name
        onboardingCompleted
        ssoVerified
      }
      organizations {
        id
        name
        role
      }
      tokens {
        accessToken
        refreshToken
        expiresIn
      }
      requiresOnboarding
      error
    }
  }
`;

export const RESET_PASSWORD_WITH_CODE = gql`
  mutation ResetPasswordWithCode($input: ResetPasswordWithCodeInput!) {
    resetPasswordWithCode(input: $input) {
      success
      error
    }
  }
`;

export const SIGN_IN_WITH_EMAIL = gql`
  mutation SignInWithEmail($input: EmailSignInInput!) {
    signInWithEmail(input: $input) {
      success
      user {
        id
        email
        name
        avatarURL
        onboardingCompleted
        ssoVerified
      }
      organizations {
        id
        name
        role
      }
      tokens {
        accessToken
        refreshToken
        expiresIn
      }
      requiresOnboarding
      error
    }
  }
`;

export const LINK_SSO_ACCOUNT = gql`
  mutation LinkSSOAccount($input: GoogleAuthInput!) {
    linkSSOAccount(input: $input) {
      success
      user {
        id
        email
        name
        ssoVerified
        authMethods {
          id
          provider
          hasPassword
        }
      }
      creditsGranted
      error
    }
  }
`;

export const SET_PASSWORD = gql`
  mutation SetPassword($input: SetPasswordInput!) {
    setPassword(input: $input) {
      success
      error
    }
  }
`;

export const COMPLETE_EMAIL_ONBOARDING = gql`
  mutation CompleteEmailOnboarding($input: EmailOnboardingInput!) {
    completeEmailOnboarding(input: $input) {
      success
      user {
        id
        email
        name
        onboardingCompleted
        ssoVerified
      }
      organization {
        id
        name
        role
        isPersonal
      }
      tokens {
        accessToken
        refreshToken
        expiresIn
      }
      error
    }
  }
`;


export const CREATE_ORGANIZATION = gql`
  mutation CreateOrganization($name: String!, $type: OrganizationType!) {
    createOrganization(name: $name, type: $type) {
      id
      name
      type
    }
  }
`;

export const UPDATE_ORGANIZATION = gql`
  mutation UpdateOrganization($id: ID!, $name: String) {
    updateOrganization(id: $id, name: $name) {
      id
      name
    }
  }
`;

export const INVITE_MEMBER = gql`
  mutation InviteMember($organizationId: ID!, $email: String!, $role: OrganizationRole) {
    inviteMember(organizationId: $organizationId, email: $email, role: $role) {
      success
      invitation {
        id
        email
        role
      }
      error
    }
  }
`;

export const REMOVE_MEMBER = gql`
  mutation RemoveMember($organizationId: ID!, $memberId: ID!) {
    removeMember(organizationId: $organizationId, memberId: $memberId) {
      success
      error
    }
  }
`;

export const RESEND_INVITATION = gql`
  mutation ResendInvitation($invitationId: ID!) {
    resendInvitation(invitationId: $invitationId) {
      success
      error
    }
  }
`;

export const ACCEPT_INVITATION = gql`
  mutation AcceptInvitation($token: String!) {
    acceptInvitation(token: $token) {
      success
      organization {
        id
        name
        role
        isPersonal
      }
      error
    }
  }
`;

export const REVOKE_INVITATION = gql`
  mutation RevokeInvitation($invitationId: ID!) {
    revokeInvitation(invitationId: $invitationId) {
      success
      error
    }
  }
`;

export const SWITCH_ORGANIZATION = gql`
  mutation SwitchOrganization($organizationId: ID!) {
    switchOrganization(organizationId: $organizationId) {
      success
      organization {
        id
        name
        role
        isPersonal
        joinedAt
      }
      tokens {
        accessToken
        refreshToken
        expiresIn
      }
      error
    }
  }
`;

export const SET_DEFAULT_ORGANIZATION = gql`
  mutation SetDefaultOrganization($organizationId: ID!) {
    setDefaultOrganization(organizationId: $organizationId) {
      success
      error
    }
  }
`;

export const LEAVE_ORGANIZATION = gql`
  mutation LeaveOrganization($organizationId: ID!) {
    leaveOrganization(organizationId: $organizationId) {
      success
      error
    }
  }
`;

export const ACCEPT_INVITATION_WITH_PASSWORD = gql`
  mutation AcceptInvitationWithPassword($input: AcceptInvitationWithPasswordInput!) {
    acceptInvitationWithPassword(input: $input) {
      success
      user {
        id
        email
        name
        onboardingCompleted
      }
      organizations {
        id
        name
        role
        isPersonal
      }
      tokens {
        accessToken
        refreshToken
        expiresIn
      }
      requiresOnboarding
      error
    }
  }
`;

export const UPDATE_TENANT_SETTINGS = gql`
  mutation UpdateTenantSettings($organizationId: ID!, $settings: TenantSettingsInput!) {
    updateTenantSettings(organizationId: $organizationId, settings: $settings) {
      success
      settings {
        max_empty_timeout
        no_moderator_timeout
        billing_contact_name
        billing_contact_email
        branding {
          logo_url
          brand_name
          accent_color
          redirect_url
          pre_join_bg_color
          welcome_message
        }
      }
      error
    }
  }
`;

// Session Mutations
export const START_SESSION = gql`
  mutation StartSession($input: StartSessionInput!) {
    startSession(input: $input) {
      id
      date
      duration
      participants
      creditsUsed
      status
      createdAt
      flowRoomName
      title
      description
      notes
    }
  }
`;

export const END_SESSION = gql`
  mutation EndSession($sessionId: ID!) {
    endSession(sessionId: $sessionId) {
      id
      status
      creditsUsed
      duration
      title
      description
      notes
    }
  }
`;

export const UPDATE_SESSION_NOTES = gql`
  mutation UpdateSessionNotes($input: UpdateSessionNotesInput!) {
    updateSessionNotes(input: $input) {
      id
      title
      description
      notes
    }
  }
`;

export const CANCEL_SESSION = gql`
  mutation CancelSession($sessionId: ID!) {
    cancelSession(sessionId: $sessionId) {
      id
      status
    }
  }
`;

export const REPORT_SESSION_ISSUE = gql`
  mutation ReportSessionIssue($input: ReportSessionIssueInput!) {
    reportSessionIssue(input: $input) {
      id
      sessionId
      userId
      participantIdentity
      issueDescription
      createdAt
      updatedAt
    }
  }
`;

export const GENERATE_CONNECTION_TOKEN = gql`
  mutation GenerateConnectionToken($roomName: String!, $participantName: String!) {
    generateConnectionToken(roomName: $roomName, participantName: $participantName) {
      serverUrl
      roomName
      participantName
      participantToken
      encryptionKey
    }
  }
`;

// Credits Mutations
export const TOP_UP_CREDITS = gql`
  mutation TopUpCredits($organizationId: ID, $amount: Int!, $paymentMethodId: String) {
    topUpCredits(organizationId: $organizationId, amount: $amount, paymentMethodId: $paymentMethodId) {
      success
      transaction {
        id
        amount
        balanceAfter
      }
      credits {
        balance
      }
    }
  }
`;

export const CREATE_PREPAID_TOPUP_CHECKOUT_SESSION = gql`
  mutation CreatePrepaidTopupCheckoutSession($input: CreatePrepaidTopupCheckoutSessionInput!) {
    createPrepaidTopupCheckoutSession(input: $input) {
      success
      url
      sessionId
      error
    }
  }
`;

// User Profile Mutations
export const UPDATE_USER_PROFILE = gql`
  mutation UpdateUserProfile($input: UpdateUserProfileInput!) {
    updateUserProfile(input: $input) {
      id
      email
      name
      phone
      avatarURL
      onboardingCompleted
      createdAt
      updatedAt
      lastLoginAt
    }
  }
`;

// Onboarding Mutations
export const COMPLETE_INDIVIDUAL_ONBOARDING = gql`
  mutation CompleteIndividualOnboarding($input: IndividualOnboardingInput!) {
    completeIndividualOnboarding(input: $input) {
      success
      user {
        id
        email
        name
        onboardingCompleted
      }
      organization {
        id
        name
        role
        isPersonal
      }
      tokens {
        accessToken
        refreshToken
        expiresIn
      }
      error
    }
  }
`;

export const COMPLETE_ORGANIZATION_ONBOARDING = gql`
  mutation CompleteOrganizationOnboarding($input: OrganizationOnboardingInput!) {
    completeOrganizationOnboarding(input: $input) {
      success
      organization {
        id
        name
        role
        isPersonal
      }
      user {
        id
        email
        name
        onboardingCompleted
      }
      tokens {
        accessToken
        refreshToken
        expiresIn
      }
      error
    }
  }
`;

// Personal Rooms Mutations
export const CREATE_PERSONAL_ROOM = gql`
  mutation CreatePersonalRoom($input: CreatePersonalRoomInput!) {
    createPersonalRoom(input: $input) {
      id
      roomName
      title
      description
      createdAt
      createdBy {
        id
        name
      }
    }
  }
`;

export const UPDATE_PERSONAL_ROOM = gql`
  mutation UpdatePersonalRoom($input: UpdatePersonalRoomInput!) {
    updatePersonalRoom(input: $input) {
      id
      roomName
      title
      description
      createdAt
      createdBy {
        id
        name
      }
    }
  }
`;


export const DELETE_PERSONAL_ROOM = gql`
  mutation DeletePersonalRoom($roomId: ID!) {
    deletePersonalRoom(roomId: $roomId)
  }
`;

export const CREATE_SUBSCRIPTION_CHECKOUT_SESSION = gql`
  mutation CreateSubscriptionCheckoutSession($input: CreateSubscriptionCheckoutSessionInput!) {
    createSubscriptionCheckoutSession(input: $input) {
      success
      url
      sessionId
      error
    }
  }
`;

export const CREATE_AUTOMATIC_BILLING_SETUP_SESSION = gql`
  mutation CreateAutomaticBillingSetupSession {
    createAutomaticBillingSetupSession {
      success
      url
      sessionId
      error
    }
  }
`;

export const SUBMIT_ENTERPRISE_INQUIRY = gql`
  mutation SubmitEnterpriseInquiry {
    submitEnterpriseInquiry {
      success
      error
    }
  }
`;

export const UPDATE_SUBSCRIPTION = gql`
  mutation UpdateSubscription($input: UpdateSubscriptionInput!) {
    updateSubscription(input: $input) {
      success
      error
      subscription {
        id
        plan
        status
        billingProvider
        pendingBillingProvider
        billingProviderSwitchAt
        monthlyCredits
        rolloverCredits
        personalRooms
        billingCycleStart
        billingCycleEnd
        priceUsd
        createdAt
        cancelledAt
        expiresAt
        scheduledChange {
          id
          sourcePlan
          targetPlan
          effectiveOn
          status
          createdAt
          updatedAt
        }
      }
    }
  }
`;

export const CREATE_BILLING_PORTAL_SESSION = gql`
  mutation CreateBillingPortalSession($returnUrl: String, $plan: SubscriptionPlan) {
    createBillingPortalSession(returnUrl: $returnUrl, plan: $plan) {
      success
      url
      error
    }
  }
`;

export const COMPLETE_STREAMING_SETUP = gql`
  mutation CompleteStreamingSetup($input: CompleteStreamingSetupInput!) {
    completeStreamingSetup(input: $input) {
      id
      streamingSetupCompleted
    }
  }
`;

export const PREPARE_STREAMING_SETUP = gql`
  mutation PrepareStreamingSetup($path: StreamingSetupPath!) {
    prepareStreamingSetup(path: $path) {
      ingressId
      roomName
      path
      url
      streamKey
      state {
        status
        error
      }
      video {
        mimeType
        codec
        averageBitrate
        width
        height
        framerate
      }
      audio {
        mimeType
        codec
        averageBitrate
        channels
        sampleRate
      }
      hasVideo
      hasAudio
      verified
      createdAt
      verifiedAt
    }
  }
`;

export const CANCEL_STREAMING_SETUP = gql`
  mutation CancelStreamingSetup($ingressId: String!) {
    cancelStreamingSetup(ingressId: $ingressId)
  }
`;

export const SKIP_STREAMING_SETUP = gql`
  mutation SkipStreamingSetup {
    skipStreamingSetup {
      id
      streamingSetupCompleted
    }
  }
`;
